import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, Redirect } from "react-router-dom";
import { Box, Grid, IconButton, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { Formik } from "formik";
import * as Yup from "yup";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import LightLayout from "components/Shared/LightLayout";
import { Input, Select } from "components/Shared/mui-formik-inputs";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import {
  getDiagnosticsSettings,
  selectDiagnosticsLoading,
  selectDiagnosticsSettings,
  updateWebDiagnosticsSettings,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
    marginBottom: 24,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 24,
  },
});

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const DiagnosticsWebSettings = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const settings = useSelector(selectDiagnosticsSettings);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!isDiagnosticsVisible(user)) {
      return;
    }
    dispatch(getDiagnosticsSettings());
  }, [dispatch, user]);

  const initialEmailSettings = useMemo(() => {
    const feedbackEmail = settings?.settings?.feedback_email || {};
    return {
      host: feedbackEmail.smtp?.host || "",
      port: feedbackEmail.smtp?.port ?? "",
      email: feedbackEmail.smtp?.email || "",
      password: feedbackEmail.smtp?.password || "",
      name: feedbackEmail.smtp?.name || "Anywize Feedback",
      security: feedbackEmail.smtp?.security || "SSL",
      recipients: (feedbackEmail.recipients || []).join(", "),
    };
  }, [settings]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        host: Yup.string().required(t("Required")),
        port: Yup.number().required(t("Required")),
        email: Yup.string().required(t("Required")),
        password: Yup.string().required(t("Required")),
        name: Yup.string(),
        security: Yup.string().required(t("Required")),
        recipients: Yup.string().required(t("Required")),
      }),
    [t],
  );

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <LightLayout doublebar loading={loading && !settings}>
        <Formik
          enableReinitialize
          initialValues={initialEmailSettings}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            const recipients = values.recipients
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);
            try {
              await dispatch(
                updateWebDiagnosticsSettings({
                  smtp: {
                    host: values.host,
                    port: Number(values.port),
                    email: values.email,
                    password: values.password,
                    name: values.name,
                    security: values.security,
                  },
                  recipients,
                }),
              );
              history.push(PATHS.diagnostics.web);
            } catch (error) {
              // error toast is handled in the slice
            }
          }}
        >
          {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <Box className={classes.actions}>
                <FormCancelSaveButton
                  disabled={isSubmitting}
                  onCancel={() => history.push(PATHS.diagnostics.web)}
                  onSave={handleSubmit}
                />
              </Box>
              <Typography className={classes.heading}>
                {t("Feedback Email Settings")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Input
                    name="host"
                    label={t("Host")}
                    value={values.host}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Input
                    name="port"
                    label={t("Port")}
                    value={values.port}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Select
                    name="security"
                    label={t("Security")}
                    value={values.security}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={[
                      { value: "SSL", label: "SSL" },
                      { value: "TLS", label: "TLS" },
                      { value: "NONE", label: "NONE" },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Input
                    name="name"
                    label={t("Name")}
                    value={values.name}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Input
                    name="email"
                    label={t("Email")}
                    value={values.email}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Input
                    name="password"
                    label={t("Password")}
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Input
                    name="recipients"
                    label={t("Recipients")}
                    value={values.recipients}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    help={t("Comma-separated emails")}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </LightLayout>
    </>
  );
};

export default DiagnosticsWebSettings;
